import { useState, useEffect, useRef } from "react";

const BrandAnimation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [glitchActive, setGlitchActive] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);

    // Random glitch effect
    const glitchInterval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 200);
    }, 5000);

    return () => clearInterval(glitchInterval);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Particle system for brand
    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = canvas.height + 10;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = -Math.random() * 2 - 1;
        this.life = 1;
        this.decay = Math.random() * 0.01 + 0.005;
        this.size = Math.random() * 3 + 1;
        this.color = Math.random() > 0.5 ? '99, 102, 241' : '139, 92, 246';
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.life -= this.decay;

        if (this.life <= 0) {
          this.reset();
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${this.color}, ${this.life * 0.6})`;
        ctx.fill();
      }
    }

    const particles = Array.from({ length: 50 }, () => new Particle());

    let animationFrameId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  return (
    <div className="relative py-32 bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 overflow-hidden">
      {/* Particle Canvas Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ opacity: 0.4 }}
      />

      {/* Animated Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.1)_2px,transparent_2px),linear-gradient(90deg,rgba(99,102,241,0.1)_2px,transparent_2px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]"></div>

      {/* Glowing Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float-delayed"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          {/* Main Brand Container */}
          <div className={`transition-all duration-1500 transform ${
            isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-20 opacity-0 scale-95'
          }`}>
            {/* Brand Name with Multiple Effects */}
            <div className="relative inline-block">
              {/* Glow Effect Behind Text */}
              <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 opacity-50 animate-pulse"></div>
              
              {/* Main Text */}
              <h1 className={`relative text-7xl md:text-9xl font-black tracking-tight ${
                glitchActive ? 'animate-glitch' : ''
              }`}>
                {/* Background Layer - Gradient */}
                <span className="absolute inset-0 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent blur-sm">
                  AdBlastX
                </span>
                
                {/* Middle Layer - Main Gradient */}
                <span className="relative bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent animate-gradient-x">
                  AdBlastX
                </span>
                
                {/* Top Layer - Shine Effect */}
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent bg-clip-text text-transparent opacity-30 animate-shine">
                  AdBlastX
                </span>
              </h1>

              {/* Underline Animation */}
              <div className="relative mt-4 h-2 overflow-hidden rounded-full">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 animate-slide-right"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 animate-slide-left opacity-50"></div>
              </div>

              {/* Floating Particles Around Brand */}
              <div className="absolute -top-8 -left-8 w-4 h-4 bg-indigo-500 rounded-full animate-float-particle"></div>
              <div className="absolute -top-4 -right-12 w-3 h-3 bg-purple-500 rounded-full animate-float-particle-delayed"></div>
              <div className="absolute -bottom-6 left-1/4 w-2 h-2 bg-pink-500 rounded-full animate-float-particle-slow"></div>
              <div className="absolute -bottom-8 right-1/3 w-3 h-3 bg-indigo-400 rounded-full animate-float-particle"></div>
            </div>

            {/* Tagline with Typewriter Effect */}
            <div className={`mt-12 transition-all duration-1500 delay-500 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <div className="relative inline-block">
                <p className="text-2xl md:text-4xl font-light text-transparent bg-clip-text bg-gradient-to-r from-gray-300 via-white to-gray-300 animate-gradient-x">
                  Transforming the Digital{" "}
                  <span className="relative inline-block">
                    <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 animate-pulse">
                      World
                    </span>
                    <span className="absolute -right-1 top-0 w-0.5 h-full bg-purple-400 animate-blink"></span>
                  </span>
                </p>
              </div>
            </div>

            {/* Tech Keywords Floating */}
            <div className={`mt-16 flex flex-wrap justify-center gap-4 transition-all duration-1500 delay-700 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              {['AI', 'META Ads', 'Google Ads', 'Software', 'Mobile Apps'].map((keyword, index) => (
                <div
                  key={keyword}
                  className="group relative"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                  <div className="relative px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 hover:border-white/40 transition-all duration-300 transform hover:scale-110">
                    <span className="text-white font-semibold text-sm md:text-base">
                      {keyword}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Animated Stats */}
            <div className={`mt-16 grid grid-cols-3 gap-8 max-w-3xl mx-auto transition-all duration-1500 delay-1000 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              {[
                { value: '500+', label: 'Projects' },
                { value: '98%', label: 'Success Rate' },
                { value: '24/7', label: 'AI Support' }
              ].map((stat, index) => (
                <div key={stat.label} className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity"></div>
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/30 transition-all duration-300 transform hover:scale-105">
                    <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-400 mt-2">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className={`mt-16 transition-all duration-1500 delay-1200 transform ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
              <button className="group relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-12 py-5 rounded-2xl text-lg font-bold transition-all duration-300 transform hover:scale-110 hover:shadow-2xl hover:shadow-purple-500/50">
                <span className="relative z-10 flex items-center justify-center">
                  Start Your Journey
                  <svg className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
                
                {/* Animated Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Shine Effect */}
                <div className="absolute inset-0 -left-full group-hover:left-full w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-all duration-700"></div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes shine {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes slide-right {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes slide-left {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes float-particle {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(10px, -10px) rotate(90deg); }
          50% { transform: translate(0, -20px) rotate(180deg); }
          75% { transform: translate(-10px, -10px) rotate(270deg); }
        }
        @keyframes float-particle-delayed {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(-10px, 10px) rotate(-90deg); }
          50% { transform: translate(0, 20px) rotate(-180deg); }
          75% { transform: translate(10px, 10px) rotate(-270deg); }
        }
        @keyframes float-particle-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-30px); }
        }
        @keyframes blink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes glitch {
          0% { transform: translate(0); }
          20% { transform: translate(-2px, 2px); }
          40% { transform: translate(-2px, -2px); }
          60% { transform: translate(2px, 2px); }
          80% { transform: translate(2px, -2px); }
          100% { transform: translate(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(90deg); }
        }
        
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        .animate-shine {
          background-size: 200% 100%;
          animation: shine 3s linear infinite;
        }
        .animate-slide-right {
          animation: slide-right 2s linear infinite;
        }
        .animate-slide-left {
          animation: slide-left 2s linear infinite;
        }
        .animate-float-particle {
          animation: float-particle 4s ease-in-out infinite;
        }
        .animate-float-particle-delayed {
          animation: float-particle-delayed 5s ease-in-out infinite;
        }
        .animate-float-particle-slow {
          animation: float-particle-slow 3s ease-in-out infinite;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        .animate-glitch {
          animation: glitch 0.3s ease-in-out;
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default BrandAnimation;
